import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";

// Centralized worksheet evaluation SSE bus. Tracks active EventSource streams
// and fans out events to any component that subscribes. Designed to mirror the
// previous EvaluateAnswerSheet behaviour, including nested completion events.

const EVENT_CHANNEL = "worksheet-sse";
const NGROK_SKIP_HEADER = "ngrok-skip-browser-warning";
const NGROK_SKIP_VALUE = "skip";

const withNgrokSkipHeader = (headers) => ({
  [NGROK_SKIP_HEADER]: NGROK_SKIP_VALUE,
  ...(headers || {}),
});

class WorksheetBus {
  static instance;

  static getInstance() {
    if (!WorksheetBus.instance) {
      WorksheetBus.instance = new WorksheetBus();
    }
    return WorksheetBus.instance;
  }

  constructor() {
    this.listeners = new Set();
    this.streams = new Map();
    this.broadcastChannel = null;

    try {
      this.broadcastChannel = new BroadcastChannel(EVENT_CHANNEL);
      this.broadcastChannel.onmessage = (event) => {
        this.emit(event.data, true);
      };
    } catch {
      // BroadcastChannel is optional; ignore if unsupported.
    }

    window.addEventListener("beforeunload", () => {
      this.disposeAll();
    });
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async submit({ endpoint, formData, token }) {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: withNgrokSkipHeader({
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      }),
      body: formData,
    });

    const rateLimitMessage =
      await extractRateLimitMessageFromResponse(response);
    if (rateLimitMessage) {
      return {
        rateLimit: true,
        message: rateLimitMessage || RATE_LIMIT_FALLBACK_MESSAGE,
        status: response.status,
      };
    }

    if (!response.ok) {
      let detail = `Upload failed: ${response.status} ${response.statusText}`;
      try {
        const body = await response.json();
        if (body?.detail) detail = body.detail;
        else if (body?.message) detail = body.message;
        else if (body?.error) detail = body.error;
      } catch {
        // Ignore JSON parse error; keep default detail.
      }
      throw new Error(detail);
    }

    const data = await response.json();
    if (!data?.requestId) {
      throw new Error("No requestId from submit API");
    }

    this.attach({
      streamBase: this.resolveStreamBase(),
      requestId: data.requestId,
      token,
    });

    return data;
  }

  attach({ streamBase, requestId, token }) {
    if (this.streams.has(requestId)) {
      return;
    }

    const controller = new AbortController();
    const url = new URL(`${streamBase}/${requestId}`, window.location.origin);
    if (token) {
      url.searchParams.set("token", token);
    }

    const headers = withNgrokSkipHeader(
      token ? { Authorization: `Bearer ${token}` } : undefined
    );
    headers.Accept = "text/event-stream";

    this.streams.set(requestId, { abortController: controller });

    fetch(url.toString(), {
      method: "GET",
      headers,
      signal: controller.signal,
    })
      .then((response) =>
        this.handleSseResponse(response, requestId, controller)
      )
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        this.emit({
          type: "evaluation_error",
          requestId,
          error:
            error?.message ||
            "Connection lost. Please check the evaluation status manually.",
        });
        this.removeStream(requestId, false);
      });
  }

  async handleSseResponse(response, requestId, controller) {
    if (!response || !response.ok || !response.body) {
      throw new Error("Failed to connect to worksheet stream");
    }

    this.emit({ type: "connected", requestId });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          buffer += decoder.decode();
          if (buffer.trim().length) {
            this.processSseBuffer(buffer, requestId);
          }
          this.removeStream(requestId, false);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        buffer = this.processSseBuffer(buffer, requestId);
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      this.emit({
        type: "evaluation_error",
        requestId,
        error:
          error?.message ||
          "Connection lost. Please check the evaluation status manually.",
      });
      this.removeStream(requestId, false);
    }
  }

  processSseBuffer(buffer, requestId) {
    if (!buffer) {
      return "";
    }

    const normalized = buffer.replace(/\r/g, "");
    let working = normalized;
    let remainder = "";

    while (true) {
      const boundaryIndex = working.indexOf("\n\n");
      if (boundaryIndex === -1) {
        remainder = working;
        break;
      }

      const eventChunk = working.slice(0, boundaryIndex);
      working = working.slice(boundaryIndex + 2);
      this.dispatchSseEvent(eventChunk, requestId);
    }

    return remainder;
  }

  dispatchSseEvent(rawEvent, requestId) {
    const trimmed = rawEvent.trim();
    if (!trimmed) {
      return;
    }

    const lines = trimmed.split("\n");
    const dataLines = [];

    for (const line of lines) {
      if (!line || line.startsWith(":")) {
        continue;
      }
      if (line.startsWith("data:")) {
        const value = line.slice(5);
        dataLines.push(value.startsWith(" ") ? value.slice(1) : value);
      }
    }

    if (!dataLines.length) {
      return;
    }

    const payloadString = dataLines.join("\n");
    this.handleSseDataString(payloadString, requestId);
  }

  handleSseDataString(payloadString, requestId) {
    let data;
    try {
      data = JSON.parse(payloadString);
    } catch {
      return;
    }

    if (!data || typeof data.type !== "string") {
      return;
    }

    if (data.type === "keepalive") {
      this.emit({ type: "keepalive", requestId });
      return;
    }

    if (data.type === "connected") {
      this.emit({ type: "connected", requestId });
      return;
    }

    if (data.type === "status_update") {
      const payload = {
        type: "status_update",
        requestId,
        status: data.status,
        percent: typeof data.percent === "number" ? data.percent : undefined,
        result: data.result,
      };

      this.emit(payload);

      if (data.status === "completed") {
        if (data.result !== undefined) {
          this.emit({
            type: "evaluation_complete",
            requestId,
            result: data.result,
            message: data.message,
          });
        }
        this.close(requestId);
      }
      return;
    }

    if (data.type === "evaluation_complete") {
      this.emit({
        type: "evaluation_complete",
        requestId,
        result: data.result,
        message: data.message,
      });
      this.close(requestId);
      return;
    }

    if (data.type === "evaluation_error") {
      this.emit({
        type: "evaluation_error",
        requestId,
        error: data.error || "Evaluation failed",
      });
      this.close(requestId);
    }
  }

  async status(fetchBase, requestId) {
    const response = await fetch(`${fetchBase}/status/${requestId}`, {
      headers: withNgrokSkipHeader(),
    });
    if (!response.ok) {
      throw new Error("Failed to get status");
    }
    return response.json();
  }

  async history(fetchBase, token, limit = 10, offset = 0) {
    const response = await fetch(
      `${fetchBase}?limit=${limit}&offset=${offset}`,
      {
        headers: withNgrokSkipHeader(
          token ? { Authorization: `Bearer ${token}` } : undefined
        ),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to get history");
    }

    return response.json();
  }

  cancel(requestId) {
    this.close(requestId);
  }

  disposeAll() {
    for (const stream of this.streams.values()) {
      try {
        if (stream?.abortController) {
          stream.abortController.abort();
        } else if (typeof stream?.close === "function") {
          stream.close();
        }
      } catch {
        // ignore
      }
    }
    this.streams.clear();
  }

  resolveStreamBase() {
    const raw = import.meta.env.VITE_APP_BASE_URL || "";
    const base = raw.replace(/\/+$/, "");
    if (base) {
      return `${base}/v2/worksheetEvaluation/stream`;
    }
    return "/v2/worksheetEvaluation/stream";
  }

  emit(event, fromBroadcast = false) {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch {
        // ignore listener errors
      }
    }

    if (!fromBroadcast && this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(event);
      } catch {
        // ignore broadcast errors
      }
    }
  }

  removeStream(requestId, abort = true) {
    const stream = this.streams.get(requestId);
    if (!stream) {
      return;
    }

    if (abort && stream?.abortController) {
      try {
        stream.abortController.abort();
      } catch {
        // ignore abort errors
      }
    } else if (abort && typeof stream?.close === "function") {
      try {
        stream.close();
      } catch {
        // ignore
      }
    }

    this.streams.delete(requestId);
  }

  close(requestId) {
    this.removeStream(requestId, true);
  }
}

export const worksheetBus = WorksheetBus.getInstance();
